import React, { useMemo, useCallback } from "react";
import {
  Card,
  CardContent,
  Grid,
  Button,
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
} from "@mui/material";
import { hasBagsLoadedData } from "../../helpers";
import { categoryByKey } from "../../helpers/ssrCategories";
import {
  REWARD_BENEFIT_KEYS,
  REWARD_TIERS,
  SHOW_PRIORITY_BOARDING_FILTER,
  getVisibleRewardBenefitFilters,
} from "../../helpers/rewardTier";
import { useFilterCardData } from "./useFilterCardData";
import { StatusFilterGroup } from "./StatusFilterGroup";
import { ServiceFilterGroup } from "./ServiceFilterGroup";
import { RewardFilterGroup } from "./RewardFilterGroup";
import { BagConnectionFilterGroup } from "./BagConnectionFilterGroup";
import { ActiveFilterChips } from "./ActiveFilterChips";
import { MealFilters } from "./MealFilters";
import { CompressToggle } from "./CompressToggle";
import { SpecialNeedsFilter } from "./SpecialNeedsFilter";

const FILTER_KEYS = {
  ALL: "ALL",
  CHECKED_IN: "CHECKED_IN",
  SPECIAL_NEEDS: "SPECIAL_NEEDS",
  ID_STAFF: "ID_STAFF",
  OTHER_INFO: "OTHER_INFO",
  BAGS_CHECKED_IN: "BAGS_CHECKED_IN",
  BAGS_LOADED: "BAGS_LOADED",
  LARGE_GROUPS: "LARGE_GROUPS",
  INBOUND_CONNECTIONS: "inboundConnections",
  OUTBOUND_CONNECTIONS: "outboundConnections",
};

const DEFAULT_FILTER = {
  statusFilter: FILTER_KEYS.ALL,
  SPECIAL_NEEDS: false,
  specialNeedsCategories: [],
  ID_STAFF: false,
  OTHER_INFO: false,
  inboundConnections: false,
  outboundConnections: false,
  selectedMeals: [],
  BAGS_CHECKED_IN: false,
  BAGS_LOADED: false,
  rewardTiers: [],
  rewardBenefits: [],
  showRewardStatus: false,
  LARGE_GROUPS: false,
  selectedGroup: null,
};

const migrateSavedFilter = (saved = {}) => {
  const merged = { ...DEFAULT_FILTER, ...saved };
  if (!Array.isArray(merged.rewardTiers)) {
    merged.rewardTiers = [];
  } else {
    merged.rewardTiers = merged.rewardTiers.filter((key) => key !== "priority");
  }
  if (!Array.isArray(merged.rewardBenefits)) {
    merged.rewardBenefits = [];
  }
  if (typeof merged.selectedGroup !== "string") {
    merged.selectedGroup = null;
  }
  merged.showRewardStatus = merged.showRewardStatus === true;
  delete merged.showRewardStatusManual;
  if (!merged.showRewardStatus) {
    merged.rewardTiers = [];
    merged.rewardBenefits = [];
  }
  if (!SHOW_PRIORITY_BOARDING_FILTER) {
    merged.rewardBenefits = merged.rewardBenefits.filter(
      (key) => key !== REWARD_BENEFIT_KEYS.PRIORITY_BOARDING
    );
  }
  delete merged.PRIORITY;
  if (!Array.isArray(merged.specialNeedsCategories)) {
    merged.specialNeedsCategories = [];
  }
  if (!merged.SPECIAL_NEEDS) {
    merged.specialNeedsCategories = [];
  }
  return merged;
};

export const FilterCard = ({
  originalPassengerList = [],
  selectedFilter = DEFAULT_FILTER,
  setSelectedFilter,
  bagFilterStats = {
    checkedInPassengers: 0,
    loadedPassengers: 0,
    matchedCheckedInBags: 0,
    matchedLoadedBags: 0,
    totalCheckedInBags: 0,
    totalLoadedBags: 0,
  },
  compressView = false,
  setCompressView,
}) => {
  const mergedFilter = migrateSavedFilter(selectedFilter);
  const {
    actualPassengers,
    mealCategoryCounts,
    mealCodeCounts,
    hasMeals,
    totalMeals,
    counts,
    largeGroups,
    rewardTierCounts,
    dynamicBenefitFilters,
    specialNeedsCategoryCounts,
  } = useFilterCardData({
    originalPassengerList,
    selectedFilter: mergedFilter,
  });

  const { checkedInPassengers = 0, loadedPassengers = 0, totalLoadedBags = 0 } =
    bagFilterStats || {};
  const showLoadedBagFilter = hasBagsLoadedData({ loadedCount: totalLoadedBags });

  const handleRadioButtonChange = useCallback(
    (e) => {
      setSelectedFilter((prev) => ({
        ...migrateSavedFilter(prev),
        statusFilter: e.target.value,
      }));
    },
    [setSelectedFilter],
  );

  const handleCheckButtonChange = useCallback(
    (e) => {
      const { name, checked } = e.target;
      setSelectedFilter((prev) => ({
        ...migrateSavedFilter(prev),
        [name]: checked,
      }));
    },
    [setSelectedFilter],
  );

  const handleShowRewardStatusToggle = useCallback(
    (e) => {
      const { checked } = e.target;
      setSelectedFilter((prev) => {
        const currentFilter = migrateSavedFilter(prev);
        if (!checked) {
          return {
            ...currentFilter,
            showRewardStatus: false,
            rewardTiers: [],
            rewardBenefits: [],
          };
        }
        return { ...currentFilter, showRewardStatus: true };
      });
    },
    [setSelectedFilter],
  );

  const handleConnectionsChildChange = useCallback(
    (e) => {
      const { name, checked } = e.target;
      setSelectedFilter((prev) => ({
        ...migrateSavedFilter(prev),
        [name]: checked,
      }));
    },
    [setSelectedFilter],
  );

  const handleLargeGroupsToggle = useCallback(
    (e) => {
      const { checked } = e.target;
      setSelectedFilter((prev) => {
        const currentFilter = migrateSavedFilter(prev);
        return {
          ...currentFilter,
          LARGE_GROUPS: checked,
          selectedGroup: checked ? currentFilter.selectedGroup : null,
        };
      });
    },
    [setSelectedFilter],
  );

  const handleMealCheckboxChange = useCallback(
    (e) => {
      const { name, checked } = e.target;
      setSelectedFilter((prev) => {
        const currentFilter = migrateSavedFilter(prev);
        const current = new Set(currentFilter.selectedMeals || []);
        if (name === "All") {
          return { ...currentFilter, selectedMeals: checked ? ["All"] : [] };
        }
        if (checked) {
          current.delete("All");
          current.add(name);
        } else {
          current.delete(name);
        }
        return { ...currentFilter, selectedMeals: Array.from(current) };
      });
    },
    [setSelectedFilter],
  );

  const handleRewardTierToggle = useCallback(
    (tierKey) => {
      setSelectedFilter((prev) => {
        const currentFilter = migrateSavedFilter(prev);
        const current = currentFilter.rewardTiers || [];
        const next = current.includes(tierKey)
          ? current.filter((key) => key !== tierKey)
          : [...current, tierKey];
        return {
          ...currentFilter,
          rewardTiers: next,
          showRewardStatus: true,
        };
      });
    },
    [setSelectedFilter],
  );

  const handleRewardBenefitToggle = useCallback(
    (benefitKey) => {
      setSelectedFilter((prev) => {
        const currentFilter = migrateSavedFilter(prev);
        const current = currentFilter.rewardBenefits || [];
        const next = current.includes(benefitKey)
          ? current.filter((key) => key !== benefitKey)
          : [...current, benefitKey];
        return {
          ...currentFilter,
          rewardBenefits: next,
          showRewardStatus: true,
        };
      });
    },
    [setSelectedFilter],
  );

  const handleSpecialNeedsParentToggle = useCallback(
    (checked) => {
      setSelectedFilter((prev) => ({
        ...migrateSavedFilter(prev),
        SPECIAL_NEEDS: checked,
        specialNeedsCategories: [],
      }));
    },
    [setSelectedFilter],
  );

  const handleSpecialNeedsCategoryToggle = useCallback(
    (categoryKey, checked) => {
      setSelectedFilter((prev) => {
        const currentFilter = migrateSavedFilter(prev);
        const selected = new Set(currentFilter.specialNeedsCategories || []);
        if (checked) selected.add(categoryKey);
        else selected.delete(categoryKey);
        const specialNeedsCategories = Array.from(selected);
        return {
          ...currentFilter,
          SPECIAL_NEEDS: true,
          specialNeedsCategories,
        };
      });
    },
    [setSelectedFilter],
  );

  const clearFilters = useCallback(() => {
    setSelectedFilter(DEFAULT_FILTER);
  }, [setSelectedFilter]);

  const activeFilterChips = useMemo(() => {
    const chips = [];
    if (mergedFilter.SPECIAL_NEEDS) {
      const selectedCategories = mergedFilter.specialNeedsCategories || [];
      if (selectedCategories.length === 0) {
      chips.push({
        key: FILTER_KEYS.SPECIAL_NEEDS,
        label: "Special needs",
          onDelete: () => handleSpecialNeedsParentToggle(false),
        });
      } else {
        selectedCategories.forEach((categoryKey) => {
          chips.push({
            key: `special-needs-${categoryKey}`,
            label: categoryByKey(categoryKey)?.label || categoryKey,
            onDelete: () => handleSpecialNeedsCategoryToggle(categoryKey, false),
          });
      });
    }
    }
    if (mergedFilter.ID_STAFF) {
      chips.push({
        key: FILTER_KEYS.ID_STAFF,
        label: "ID Staff",
        onDelete: () =>
          handleCheckButtonChange({
            target: { name: FILTER_KEYS.ID_STAFF, checked: false },
          }),
      });
    }
    if (mergedFilter.OTHER_INFO) {
      chips.push({
        key: FILTER_KEYS.OTHER_INFO,
        label: "Other info",
        onDelete: () =>
          handleCheckButtonChange({
            target: { name: FILTER_KEYS.OTHER_INFO, checked: false },
          }),
      });
    }
    if (mergedFilter.LARGE_GROUPS) {
      const selectedGroup = largeGroups.find(
        ({ key }) => key === mergedFilter.selectedGroup
      );
      chips.push({
        key: FILTER_KEYS.LARGE_GROUPS,
        label: selectedGroup
          ? `Large Groups: ${selectedGroup.label}`
          : "Large Groups",
        onDelete: () =>
          handleLargeGroupsToggle({
            target: { checked: false },
          }),
      });
    }
    (mergedFilter.rewardTiers || []).forEach((tierKey) => {
      const tier = REWARD_TIERS.find((item) => item.key === tierKey);
      chips.push({
        key: `reward-tier-${tierKey}`,
        label: tier?.displayName || tierKey,
        onDelete: () => handleRewardTierToggle(tierKey),
      });
    });
    (mergedFilter.rewardBenefits || []).forEach((benefitKey) => {
      const benefit = dynamicBenefitFilters.find(
        (item) => item.key === benefitKey,
      );
      chips.push({
        key: `reward-benefit-${benefitKey}`,
        label: benefit?.label || benefitKey,
        onDelete: () => handleRewardBenefitToggle(benefitKey),
      });
    });
    if (mergedFilter.BAGS_CHECKED_IN) {
      chips.push({
        key: FILTER_KEYS.BAGS_CHECKED_IN,
        label: "Bags checked-in (passengers)",
        onDelete: () =>
          handleCheckButtonChange({
            target: { name: FILTER_KEYS.BAGS_CHECKED_IN, checked: false },
          }),
      });
    }
    if (mergedFilter.BAGS_LOADED && showLoadedBagFilter) {
      chips.push({
        key: FILTER_KEYS.BAGS_LOADED,
        label: "Bags loaded (passengers)",
        onDelete: () =>
          handleCheckButtonChange({
            target: { name: FILTER_KEYS.BAGS_LOADED, checked: false },
          }),
      });
    }
    if (mergedFilter.inboundConnections && mergedFilter.outboundConnections) {
      chips.push({
        key: "BOTH_CONNECTIONS",
        label: "Inbound + Outbound connections",
        onDelete: () => {
          handleConnectionsChildChange({
            target: { name: FILTER_KEYS.INBOUND_CONNECTIONS, checked: false },
          });
          handleConnectionsChildChange({
            target: { name: FILTER_KEYS.OUTBOUND_CONNECTIONS, checked: false },
          });
        },
      });
    } else if (mergedFilter.inboundConnections) {
      chips.push({
        key: FILTER_KEYS.INBOUND_CONNECTIONS,
        label: "Inbound connections",
        onDelete: () =>
          handleConnectionsChildChange({
            target: { name: FILTER_KEYS.INBOUND_CONNECTIONS, checked: false },
          }),
      });
    } else if (mergedFilter.outboundConnections) {
      chips.push({
        key: FILTER_KEYS.OUTBOUND_CONNECTIONS,
        label: "Outbound connections",
        onDelete: () =>
          handleConnectionsChildChange({
            target: { name: FILTER_KEYS.OUTBOUND_CONNECTIONS, checked: false },
          }),
      });
    }

    const selectedMeals = mergedFilter.selectedMeals || [];
    if (selectedMeals.includes("All")) {
      chips.push({
        key: "ALL_MEALS",
        label: "All meals",
        onDelete: () =>
          handleMealCheckboxChange({ target: { name: "All", checked: false } }),
      });
    } else if (selectedMeals.length > 0) {
      selectedMeals.forEach((meal) => {
        chips.push({
          key: `MEAL_${meal}`,
          label: `${meal} meals`,
          onDelete: () =>
            handleMealCheckboxChange({
              target: { name: meal, checked: false },
            }),
        });
      });
    }
    return chips;
  }, [
    mergedFilter,
    dynamicBenefitFilters,
    largeGroups,
    handleCheckButtonChange,
    handleSpecialNeedsParentToggle,
    handleSpecialNeedsCategoryToggle,
    handleConnectionsChildChange,
    handleLargeGroupsToggle,
    handleMealCheckboxChange,
    handleRewardTierToggle,
    handleRewardBenefitToggle,
    showLoadedBagFilter,
  ]);

  const allPassengerBreakdown = useMemo(() => {
    const infants = actualPassengers.filter((p) => p.isInfant).length;
    return {
      infants,
      adults: actualPassengers.length - infants,
    };
  }, [actualPassengers]);

  const rewardTierFilters = useMemo(
    () =>
      REWARD_TIERS.map((tier) => ({
        key: tier.key,
        label: tier.displayName,
        count: rewardTierCounts[tier.key] || 0,
        color: tier.color,
      })),
    [rewardTierCounts],
  );

  const rewardBenefitFilters = useMemo(
    () =>
      getVisibleRewardBenefitFilters(
        dynamicBenefitFilters.map((benefit) => ({
          key: benefit.key,
          label: benefit.label,
          count: benefit.count,
        }))
      ),
    [dynamicBenefitFilters],
  );

  const serviceFilters = useMemo(
    () => [
      {
        key: FILTER_KEYS.ID_STAFF,
        label: "ID Staff",
        count: counts.idStaffCount,
        checked: mergedFilter.ID_STAFF,
      },
      {
        key: FILTER_KEYS.OTHER_INFO,
        label: "Other info",
        count: counts.otherInfoCount,
        checked: mergedFilter.OTHER_INFO,
      },
    ],
    [counts, mergedFilter],
  );

  const bagFilters = useMemo(() => {
    const filters = [
      {
        key: FILTER_KEYS.BAGS_CHECKED_IN,
        label: "Passengers with bags",
        count: checkedInPassengers,
        checked: mergedFilter.BAGS_CHECKED_IN,
      },
    ];
    if (showLoadedBagFilter) {
      filters.push({
        key: FILTER_KEYS.BAGS_LOADED,
        label: "Passengers with loaded bags",
        count: loadedPassengers,
        checked: mergedFilter.BAGS_LOADED,
      });
    }
    return filters;
  }, [checkedInPassengers, loadedPassengers, mergedFilter, showLoadedBagFilter]);

  const connectionFilters = useMemo(
    () => [
      {
        key: FILTER_KEYS.INBOUND_CONNECTIONS,
        label: "Inbound connections",
        count: counts.inboundCount,
        checked: mergedFilter.inboundConnections,
      },
      {
        key: FILTER_KEYS.OUTBOUND_CONNECTIONS,
        label: "Outbound connections",
        count: counts.outboundCount,
        checked: mergedFilter.outboundConnections,
      },
    ],
    [counts, mergedFilter],
  );

  return (
    <Card elevation={0}>
      <CardContent
        sx={{
          py: 1,
          "&:last-child": { pb: 1.5 },
          "& .MuiCheckbox-root, & .MuiRadio-root": {
            py: 0.25,
            "& .MuiSvgIcon-root": { fontSize: 18 },
          },
          "& .MuiFormControlLabel-root:has(.MuiCheckbox-root), & .MuiFormControlLabel-root:has(.MuiRadio-root)":
            {
              ml: -0.25,
            },
        }}
      >
        <Grid container>
          <Grid>
            <StatusFilterGroup
              value={mergedFilter.statusFilter}
              onStatusChange={handleRadioButtonChange}
              labels={{
                ALL: FILTER_KEYS.ALL,
                CHECKED_IN: FILTER_KEYS.CHECKED_IN,
              }}
              totalAdults={allPassengerBreakdown.adults}
              totalInfants={allPassengerBreakdown.infants}
              checkedInAdults={counts.countAdultsCheckedIn}
              checkedInInfants={counts.countInfantsCheckedIn}
            />
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={mergedFilter.showRewardStatus}
                    onChange={handleShowRewardStatusToggle}
                    name="showRewardStatus"
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    Reward passengers
                  </Typography>
                }
              />
            </FormGroup>
            {mergedFilter.showRewardStatus ? (
              <Box sx={{ pl: 3.5, pb: 0.5 }}>
              <RewardFilterGroup
                title="Reward passengers"
                filters={rewardTierFilters}
                selectedKeys={mergedFilter.rewardTiers || []}
                onToggle={handleRewardTierToggle}
              />
              <RewardFilterGroup
                title="Filter by reward benefits"
                filters={rewardBenefitFilters}
                selectedKeys={mergedFilter.rewardBenefits || []}
                onToggle={handleRewardBenefitToggle}
              />
            </Box>
            ) : null}
            <SpecialNeedsFilter
              count={counts.specialNeedsCount}
              categoryCounts={specialNeedsCategoryCounts}
              checked={mergedFilter.SPECIAL_NEEDS}
              selectedCategories={mergedFilter.specialNeedsCategories}
              onParentToggle={handleSpecialNeedsParentToggle}
              onCategoryToggle={handleSpecialNeedsCategoryToggle}
            />
            <ServiceFilterGroup
              filters={serviceFilters}
              onToggle={handleCheckButtonChange}
            />
            <BagConnectionFilterGroup
              bagFilters={bagFilters}
              connectionFilters={connectionFilters}
              onBagToggle={handleCheckButtonChange}
              onConnectionToggle={handleConnectionsChildChange}
            />
            {largeGroups.length > 0 && (
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={mergedFilter.LARGE_GROUPS}
                      onChange={handleLargeGroupsToggle}
                      name={FILTER_KEYS.LARGE_GROUPS}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      Large Groups
                    </Typography>
                  }
                />
              </FormGroup>
            )}
          </Grid>
        </Grid>

        <ActiveFilterChips chips={activeFilterChips} />

        <MealFilters
          hasMeals={hasMeals}
          mealCategoryCounts={mealCategoryCounts}
          mealCodeCounts={mealCodeCounts}
          totalMeals={totalMeals}
          selectedMeals={mergedFilter.selectedMeals}
          onMealToggle={handleMealCheckboxChange}
        />

        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          sx={{ mt: 0.5 }}
        >
          <Grid>
            <Button size="small" onClick={clearFilters}>
              Clear filter
            </Button>
        </Grid>
          <Grid>

        <CompressToggle
          compressView={compressView}
          onChange={setCompressView}
        />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
